"use client";

import React from "react";
import { useMarkupStore } from "../store";
import { TabletIcon, MobileIcon } from "./Icons";
import { cn } from "../utils";
import { DEVICE_PRESETS } from "../utils/deviceFrame";
import type { DeviceType, ViewportPreset } from "../types";

export const DeviceIcon: React.FC<{ deviceType: DeviceType; className?: string }> = ({
    deviceType,
    className,
}) =>
    deviceType === "tablet" ? (
        <TabletIcon className={className} />
    ) : (
        <MobileIcon className={className} />
    );

/** The device list, shared by the entry button and the preview toolbar. */
export const PresetMenu: React.FC<{
    activeName?: string;
    onSelect: (preset: ViewportPreset) => void;
}> = ({ activeName, onSelect }) => (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-xl p-2 min-w-[220px]">
        {DEVICE_PRESETS.map((preset) => (
            <button
                key={preset.name}
                type="button"
                onClick={() => onSelect(preset)}
                className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-left",
                    activeName === preset.name
                        ? "bg-matter-ink/10"
                        : "hover:bg-matter-ink/5",
                )}
            >
                <DeviceIcon
                    deviceType={preset.deviceType}
                    className="w-4 h-4 text-gray-400 shrink-0"
                />
                <span className="flex-1 text-sm! font-medium text-gray-700">
                    {preset.name}
                </span>
                <span className="text-xs! text-gray-400 tabular-nums">
                    {preset.width}×{preset.height}
                </span>
            </button>
        ))}
    </div>
);

/** Close a dropdown on outside click / Escape. */
export function useDismiss(
    open: boolean,
    setOpen: (open: boolean) => void,
    ref: React.RefObject<HTMLElement | null>,
) {
    React.useEffect(() => {
        if (!open) return;
        // composedPath, not e.target: in the script-tag build the widget lives
        // in a Shadow DOM, where a document listener sees every click as coming
        // from the shadow host — so `contains(e.target)` was always false and
        // the menu shut on the very click meant to pick from it.
        const onOutside = (e: MouseEvent) => {
            if (ref.current && !e.composedPath().includes(ref.current)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            setOpen(false);
            // Claimed, so the preview doesn't also treat it as "exit".
            e.preventDefault();
        };
        document.addEventListener("mousedown", onOutside);
        document.addEventListener("keydown", onKey, true);
        return () => {
            document.removeEventListener("mousedown", onOutside);
            document.removeEventListener("keydown", onKey, true);
        };
    }, [open, setOpen, ref]);
}

/**
 * "Responsive" entry point, shown above the page while the panel is open.
 * Once a device is picked, `DeviceStage` takes over with its own toolbar.
 */
export const ViewportControls: React.FC = () => {
    const { viewportMode, setViewportMode, isOpen, isSteppingAside } = useMarkupStore();
    const [isExpanded, setIsExpanded] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    useDismiss(isExpanded, setIsExpanded, dropdownRef);

    if (!isOpen || viewportMode || isSteppingAside) return null;

    return (
        <div
            ref={dropdownRef}
            className="fixed top-4 left-1/2 -translate-x-1/2 z-[999999] select-none"
            data-markup-viewport
        >
            <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-2xl text-sm! font-medium bg-white border transition-all shadow-md",
                    isExpanded
                        ? "border-matter-ink text-black shadow-lg shadow-matter-ink/10"
                        : "border-gray-200 text-gray-600 hover:border-matter-ink hover:text-black hover:shadow-lg hover:shadow-matter-ink/10",
                )}
            >
                <MobileIcon
                    className={cn(
                        "w-4 h-4 transition-colors",
                        isExpanded ? "text-matter-accent" : "text-gray-400",
                    )}
                />
                <span>Responsive</span>
                <svg
                    className={cn(
                        "w-3 h-3 text-gray-400 transition-transform duration-200",
                        isExpanded && "rotate-180",
                    )}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isExpanded && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2">
                    <PresetMenu
                        onSelect={(preset) => {
                            setViewportMode({ ...preset });
                            setIsExpanded(false);
                        }}
                    />
                </div>
            )}
        </div>
    );
};
